import { ValueOf } from "type-fest";

import {
  defaultPurchasingPriority,
  determineEffectivePurchasingPriority,
  EffectivePriority,
  getChildPurchasingPriorityOverrides,
} from "@/features/purchasing-priority/purchasing-priority-utils";
import { hasBom } from "@/utils/sos/utilities";
import { sortedFindBy } from "@/utils/util";
import { CategorizedCosts } from "../../categeorized-costs";
import { BomRecord, BomRecordsForParents } from "./bom-records";
import { CostTaxBreakdown } from "./cost-tax-breakdown";
import { Item } from "./items";
import { PurchasingPriorities } from "./purchasing-priorities";

export interface BomNode {
  lineNumber: number;
  item: Item;
  /** The quantity saved for this line item in the BOM */
  quantity: number;
  /** The quantity for this line item multiplied by the parent quantity */
  totalQuantity: number;
  notes: string | null;
  isAssembly: boolean;
  recursiveDepth: number;

  /** The unique tree path for this node, using commas (,) as path separators */
  path: string;

  /**
   * Indicates whether an assembly is missing a labour estimate. Is only true for assemblies and when the assembly BOM
   * has no labour estimate, or a -CM contract manufacturing expense line item.
   */
  missingLabourEstimate: boolean;

  /**
   * For non-assemblies, the purchase cost of a single unit from the preferred vendor, as determined by the 'items'
   * import stage.
   *
   * For assemblies, the sum total of the `unitCost` value on it's children.
   *
   * This is also the sum of all the costs in the `categorizedUnitCosts` object.
   */
  unitCost: CostTaxBreakdown;

  /**
   * The unit cost of this node, broken down by the category assigned to the items that make up the cost.
   */
  categorizedUnitCosts: CategorizedCosts;

  /**
   * The `unitCost` multiplied by the quantity for this node. This is also the sum of all the costs in the
   * `categorizedLineItemCosts` object.
   */
  lineItemCost: CostTaxBreakdown;

  /**
   * The categorized unit costs for this node, multiplied by the quantity for this node.
   */
  categorizedLineItemCosts: CategorizedCosts;

  /** Purchasing priority information for this node */
  purchasingPriority: EffectivePriority;

  children: BomNode[];
  asciiTreePrefix: string;
}

export type AsciiTreeComponent = ValueOf<typeof ASCII_TREE_STRINGS>;

const ASCII_TREE_STRINGS = {
  CHILD: "├─ ",
  LAST_CHILD: "└─ ",
  DIRECTORY: "│  ",
  EMPTY: "   ",
} as const;

export class BomHierarchyGenerator {
  constructor(private readonly purchasingPriorities: PurchasingPriorities) {}

  public generate({ parents, bomRecordsByParentPath }: BomRecordsForParents): BomNode[] {
    // Do nothing if no parents were specified
    if (!parents.length) return [];

    // Create dummy BOM records for the provided parents, then recursively generate the BOM hierarchy
    return parents
      .map<BomRecord>((parent, index) => ({
        assemblyId: NaN,
        lineNumber: index + 1,
        componentId: parent.item.id,
        item: parent.item,
        quantity: parent.quantity,
        notes: "",
        path: [],
        depth: -1,
      }))
      .map((x) =>
        this.supabaseBomRecordToHierarchicalBomNode(x, [], 1, defaultPurchasingPriority, bomRecordsByParentPath),
      );
  }

  private supabaseBomRecordToHierarchicalBomNode(
    currentRecord: BomRecord,
    asciiTreeComponents: ValueOf<typeof ASCII_TREE_STRINGS>[],
    parentTotalQuantity: number,
    parentPurchasingPriority: Omit<EffectivePriority, "childOverrides">,
    bomRecordsByParentPath: Record<string, BomRecord[]>,
  ): BomNode {
    // Determine the effective purchasing priority for this node
    const pathString = [...currentRecord.path, currentRecord.componentId].join(",");
    const nodeTemplatePriorityRecord = sortedFindBy(
      this.purchasingPriorities.templatePriorities,
      { path: pathString },
      (x) => x.path,
    );
    const nodeJobPriorityRecord = sortedFindBy(
      this.purchasingPriorities.salesOrderPriorities,
      { path: pathString },
      (x) => x.path,
    );
    const purchasingPriority = determineEffectivePurchasingPriority(
      parentPurchasingPriority,
      nodeTemplatePriorityRecord,
      nodeJobPriorityRecord,
    );

    // Get the BOM records for this item (will be an empty array for non-assemblies)
    // NOTE: We need to check the item type here because non-assemblies may still have BOM records (i.e. if they used to
    // be an assembly but the item type was later changed)
    const bomRecords = hasBom(currentRecord.item.type) ? bomRecordsByParentPath[pathString] : [];

    // Recursively generate the BOM for each child
    const totalQuantity = currentRecord.quantity * parentTotalQuantity;
    const children = (bomRecords ?? []).map((x, index, arr) => {
      // Determine the components of the ASCII tree for this child
      const childAsciiTreeComponents = BomHierarchyGenerator.getAsciiTreeComponents(
        asciiTreeComponents,
        index === arr.length - 1,
      );

      return this.supabaseBomRecordToHierarchicalBomNode(
        x,
        childAsciiTreeComponents,
        totalQuantity,
        purchasingPriority,
        bomRecordsByParentPath,
      );
    });

    // Calculate costs for the current item
    const item = currentRecord.item;
    const isAssembly = hasBom(item.type);
    const categorizedUnitCosts = this.calculateCategorizedUnitCosts(item, children);
    const categorizedLineItemCosts = categorizedUnitCosts.multiply(currentRecord.quantity);
    const unitCost = categorizedUnitCosts.getTotalCost();
    const lineItemCost = categorizedLineItemCosts.getTotalCost();

    // Collect all child nodes with overridden priorities
    const childOverrides = getChildPurchasingPriorityOverrides(children);

    // Create a BOM node for the current item
    return {
      lineNumber: currentRecord.lineNumber,
      item,
      path: pathString,
      missingLabourEstimate: this.isMissingLabourEstimate(item, children),
      purchasingPriority: { ...purchasingPriority, childOverrides },
      quantity: currentRecord.quantity,
      totalQuantity,
      notes: currentRecord.notes,
      isAssembly,
      recursiveDepth: currentRecord.depth + 1,
      categorizedUnitCosts,
      categorizedLineItemCosts,
      unitCost,
      lineItemCost,
      children,
      asciiTreePrefix: asciiTreeComponents.join(""),
    };
  }

  private isMissingLabourEstimate(item: Item, children: BomNode[]): boolean {
    return hasBom(item.type) && !children.some((x) => x.item.type === "Labor" || x.item.name.endsWith("-CM"));
  }

  private calculateCategorizedUnitCosts(item: Item, children: BomNode[]): CategorizedCosts {
    if (!hasBom(item.type)) {
      // Non-assemblies use the purchase cost for the preferred vendor
      // The category is assigned based on the item type (all other categories are zero)
      const category = CategorizedCosts.getItemCostCategory(item);
      return new CategorizedCosts({ [category]: item.preferredPurchaseCost });
    } else {
      // Calculate assembly costs as the sum of its line items
      return children
        .map((x) => x.categorizedUnitCosts.multiply(x.quantity))
        .reduce((sum, x) => sum.add(x), CategorizedCosts.ZERO);
    }
  }

  /**
   * Get the ASCII tree components for a node based on the ASCII tree components of its parent and whether it is the
   * last child.
   */
  public static getAsciiTreeComponents(
    parentComponents: AsciiTreeComponent[],
    isLastChild: boolean,
  ): AsciiTreeComponent[] {
    // Swap out the parent's last leaf component for the appropriate tree component
    const childComponents = [...parentComponents];
    if (childComponents.length) {
      childComponents.push(
        childComponents.pop() === ASCII_TREE_STRINGS.LAST_CHILD ?
          ASCII_TREE_STRINGS.EMPTY
        : ASCII_TREE_STRINGS.DIRECTORY,
      );
    }

    // Add a leaf segment for the child
    childComponents.push(isLastChild ? ASCII_TREE_STRINGS.LAST_CHILD : ASCII_TREE_STRINGS.CHILD);
    return childComponents;
  }
}
